/**
 * What this browser can do with the motion timeline, and the copy that says
 * so. Every unsupported path tells the user instead of failing silently.
 *
 * iOS (including iPadOS, whose UA says Macintosh) can preview but must not
 * export: WebCodecs there is memory-capped and long videos kill the tab.
 * Desktop Safari can export MP4 but has a history of flaky canvas frames, so
 * it gets a warning rather than a block.
 */

export type MotionBrowserId = 'chromium' | 'firefox' | 'safari' | 'ios' | 'other';

export interface MotionBrowser {
  id: MotionBrowserId;
  /** False on phones, tablets and iOS: preview there, export on a desktop. */
  exportOnDevice: boolean;
  /** Timeline banner. Null when there is nothing to warn about. */
  banner: string | null;
}

export const MOTION_EXPORT_DESKTOP_COPY =
  'Export video on a desktop browser. Preview works on this device.';

const IOS_BANNER =
  'Preview works on iPhone and iPad. Export from a desktop browser — long videos run out of memory here.';
const SAFARI_BANNER =
  'Safari can export MP4. If an export fails or plays back wrong, use Chrome or Edge.';
const LEGACY_BANNER =
  'This browser will record the video in real time at a smaller size instead of encoding it directly.';

export interface BrowserEnv {
  ua: string;
  touchPoints: number;
  hasVideoEncoder: boolean;
}

/** Above this, an in-memory MP4 risks exhausting the tab. Stream it to disk. */
export const IN_MEMORY_EXPORT_LIMIT_BYTES = 300_000_000;

function isIos(ua: string, touchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports a desktop Macintosh UA; touch points give it away.
  return /Macintosh/.test(ua) && touchPoints > 1;
}

function isSafari(ua: string): boolean {
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Android|CriOS|FxiOS/.test(ua);
}

export function classifyBrowser(env: BrowserEnv): MotionBrowser {
  if (isIos(env.ua, env.touchPoints)) {
    return { id: 'ios', exportOnDevice: false, banner: IOS_BANNER };
  }
  if (isSafari(env.ua)) {
    return { id: 'safari', exportOnDevice: true, banner: SAFARI_BANNER };
  }
  if (/Firefox/.test(env.ua)) {
    return { id: 'firefox', exportOnDevice: true, banner: null };
  }
  if (/Chrome|Chromium|Edg/.test(env.ua)) {
    return {
      id: 'chromium',
      exportOnDevice: true,
      banner: env.hasVideoEncoder ? null : LEGACY_BANNER,
    };
  }
  return {
    id: 'other',
    exportOnDevice: true,
    banner: env.hasVideoEncoder ? null : LEGACY_BANNER,
  };
}

/** This tab's browser. `exportOnDevice` is also false away from the desktop layout. */
export function detectMotionBrowser(): MotionBrowser {
  if (typeof navigator === 'undefined') {
    return { id: 'other', exportOnDevice: true, banner: null };
  }
  return classifyBrowser({
    ua: navigator.userAgent,
    touchPoints: navigator.maxTouchPoints || 0,
    hasVideoEncoder: typeof VideoEncoder !== 'undefined',
  });
}

export function exportAllowed(layout: string, browser: MotionBrowser = detectMotionBrowser()): boolean {
  return layout === 'desktop' && browser.exportOnDevice;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{ description?: string; accept: Record<string, string[]> }>;
}

type SaveFilePicker = (options: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;

/**
 * File System Access API. Absent from this project's DOM lib, and missing
 * entirely in Firefox and Safari — those keep the file in memory.
 */
export function saveFilePicker(): SaveFilePicker | null {
  if (typeof window === 'undefined') return null;
  const picker = (window as Window & { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker;
  return picker ? picker.bind(window) : null;
}
